import { useCallback } from 'react';
import {
  ReputationNotificationsLocationUserSetting,
  SettingType_Enum,
} from '@weave/schema-gen-ts/dist/schemas/messaging/settings/v1/settings.pb';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { MessagingSettingsV1 } from '@frontend/api-messaging-settings';
import { ReviewsTypes } from '@frontend/api-reviews';
import { ReviewsAggregatorV1 } from '@frontend/api-reviews-aggregator';
import { getUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { notificationSounds, useReviewNotification } from '@frontend/notifications';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { GetWeavePopNotificationByType } from '@frontend/types';
import { GetWebsocketEventHandler, useWebsocketEventSubscription } from '@frontend/websocket';

export const useReviewEventSubscription = () => {
  const { create } = useReviewNotification();
  const { t } = useTranslation('notifications');
  const user = getUser();
  const { selectedLocationIds, selectedOrgId } = useAppScopeStore();
  const { getFeatureFlagLocationMap, getFeatureFlagValue } = useScopedAppFlagStore();
  const locationReputationSettingsFlags = getFeatureFlagLocationMap('nwx:messaging-user-settings-reviews');
  const shouldQueryReputationSettings = getFeatureFlagValue('nwx:messaging-user-settings-reviews', 'ANY');
  const shouldUseLegacyHandler = !getFeatureFlagValue('review-notifications', 'ALL');

  const defaultReputationSettingsQuery = MessagingSettingsV1.Queries.useTypesQuery(
    {},
    {
      enabled: shouldQueryReputationSettings,
      select: (data) => {
        const defaultSetting = data.types.find(
          (item) => item.settingType === SettingType_Enum.REPUTATION_NOTIFICATIONS_LOCATION_USER_SETTING
        )?.defaultSetting;
        if (defaultSetting && 'reputationNotificationsLocationUserSetting' in defaultSetting) {
          return defaultSetting.reputationNotificationsLocationUserSetting;
        }
        return undefined;
      },
    }
  );

  const reputationSettingsQuery = MessagingSettingsV1.Queries.useListQuery(
    {
      settingType: SettingType_Enum.REPUTATION_NOTIFICATIONS_LOCATION_USER_SETTING,
      locationIds: selectedLocationIds,
      orgId: selectedOrgId,
      userId: user?.userID ?? '',
    },
    {
      enabled: shouldQueryReputationSettings && !!user?.userID,
      select: (data) => {
        return selectedLocationIds.reduce<Record<string, ReputationNotificationsLocationUserSetting>>(
          (acc, locationId) => {
            const matchingSetting = data.settings.find((item) => item.locationId === locationId);

            // If no setting has been configured, there will be no setting for that location in the list
            // So we will populate that location's setting with the default if it's not in the list
            if (!matchingSetting || !('reputationNotificationsLocationUserSetting' in matchingSetting.setting)) {
              acc[locationId] =
                defaultReputationSettingsQuery.data ??
                MessagingSettingsV1.Constants.DEFAULT_SETTINGS.reputationNotificationsLocationUserSetting;
              return acc;
            }

            acc[locationId] = matchingSetting.setting.reputationNotificationsLocationUserSetting;
            return acc;
          },
          {}
        );
      },
    }
  );

  const reputationUserSettingsQuery = MessagingSettingsV1.Queries.useGetQuery(
    {
      settingType: SettingType_Enum.REPUTATION_USER_SETTING,
      userId: user?.userID ?? '',
      orgId: selectedOrgId,
    },
    {
      enabled: shouldQueryReputationSettings && !!user?.userID,
      select: (data) => {
        if ('reputationUserSetting' in data.setting) {
          return data.setting.reputationUserSetting;
        }
        return undefined;
      },
    }
  );

  const legacyNotificationHandler = useCallback<GetWebsocketEventHandler<'ReviewsNotification'>>(
    (payload) => {
      if (!shouldUseLegacyHandler) return;
      const notification = {
        id: payload.id?.toString(),
        timestamp: Date.now(),
        type: 'review-new',
        payload: {
          title: t('New Review'),
          body: payload.params.review,
          author: payload.params.author,
          siteName: payload.params.siteName,
          reviewId: payload.params.reviewId,
          rating: payload.params.rating,
          recommendationType: payload.params.recommendationType,
        },
        state: {
          paused: false,
          status: 'unread',
        },
      } satisfies GetWeavePopNotificationByType<'review-new'>;

      create(notification, {
        os: {
          title: t('New Review'),
          body: payload.params.review,
        },
      });
    },
    [create]
  );

  const { invalidateQueries } = ReviewsAggregatorV1._QueryUpdaters.useQueryUpdaters();
  const handleNotification = useCallback<GetWebsocketEventHandler<NotificationType.REVIEWS>>(
    (payload) => {
      if (shouldUseLegacyHandler) return;
      const { review, author, siteName, reviewId, rating, recommendationType, locationId } = payload.params;
      // TODO: improve this by updating the cache instead of invalidating
      invalidateQueries({
        endpointName: 'GetAggregatedReviews',
        queryFilters: {
          predicate: ({ queryKey }) => {
            const request = queryKey[2];
            if (request && request.locations?.length && locationId && request.locations.includes(locationId))
              return true;
            return false;
          },
        },
      });
      if (!review || !author || !siteName || !reviewId || recommendationType === undefined || rating === undefined)
        return;
      let typedSiteName: 'google' | 'facebook' | 'private' | undefined;
      if (siteName === 'google' || siteName === 'facebook' || siteName === 'private') {
        typedSiteName = siteName;
      }
      if (!typedSiteName) return;

      const title =
        siteName === 'google'
          ? t('Google Review Submitted')
          : siteName === 'facebook'
          ? t('Facebook Review Submitted')
          : t('New Review');

      const notification: Parameters<typeof create>[0] = {
        id: payload.id?.toString(),
        timestamp: Date.now(),
        type: 'review-new',
        payload: {
          title,
          body: review,
          author: author,
          reviewId: reviewId,
          siteName,
          rating,
          recommendationType,
        },
        state: {
          paused: false,
          status: 'unread',
        },
      };

      const soundId =
        notificationSounds.find(
          ({ name }) => name.toLowerCase() === reputationUserSettingsQuery.data?.notificationSound.toLowerCase()
        )?.id ?? '';

      const options: Parameters<typeof create>[1] = {
        os: {
          title: t('New Review'),
          body: payload.params.review,
        },
        force: true,
        soundId,
      };

      const newSettingsAreEnabled = locationId ? !!locationReputationSettingsFlags[locationId]?.value : false;
      if (!newSettingsAreEnabled) return;

      const locationSetting = locationId
        ? reputationSettingsQuery.data?.[locationId]
        : defaultReputationSettingsQuery.data;

      if (!locationSetting) return;

      // Notifiation settings threshold checks gauntlet
      if (payload.params.siteName === 'google' && rating !== undefined) {
        const { googleReviewSettings } = locationSetting;

        if (!googleReviewSettings.enableGoogleReviewNotifications) {
          return;
        }

        if (
          googleReviewSettings.enableGoogleReviewHigherNotifications ||
          googleReviewSettings.enableGoogleReviewLowerNotifications
        ) {
          const ratingIsInThreshold =
            (googleReviewSettings.enableGoogleReviewHigherNotifications &&
              rating >= googleReviewSettings.googleReviewHigherThreshold) ||
            (googleReviewSettings.enableGoogleReviewLowerNotifications &&
              rating <= googleReviewSettings.googleReviewLowerThreshold);

          if (!ratingIsInThreshold) return;
        }
      } else if (payload.params.siteName === 'facebook') {
        const { facebookReviewSettings } = locationSetting;

        // Check if Facebook notifications are enabled at all
        if (!facebookReviewSettings.enableFacebookReviewNotifications) {
          return;
        }

        // Check recommended status settings
        if (
          payload.params.recommendationType === ReviewsTypes.Recommendation.Recommended &&
          !facebookReviewSettings.notifyRecommended
        ) {
          return;
        }

        // Check not recommended status settings
        if (
          payload.params.recommendationType === ReviewsTypes.Recommendation.NotRecommended &&
          !facebookReviewSettings.notifyNotRecommended
        ) {
          return;
        }
      } else if (payload.params.siteName === 'private') {
        if (!locationSetting.privateReviewSettings.enablePrivateReviewNotifications) {
          return;
        }
      }

      create(notification, options);
    },
    [
      create,
      JSON.stringify(defaultReputationSettingsQuery.data),
      JSON.stringify(reputationSettingsQuery.data),
      reputationUserSettingsQuery.data?.notificationSound,
      JSON.stringify(locationReputationSettingsFlags),
    ]
  );

  const legacySubscription = useWebsocketEventSubscription('ReviewsNotification', legacyNotificationHandler);
  const newSubscription = useWebsocketEventSubscription(NotificationType.REVIEWS, handleNotification);

  return shouldUseLegacyHandler ? legacySubscription : newSubscription;
};
